import { useCallback, useState } from "react";
import useFirestore from "./useFirestore";

/**
 * Dismiss state for a promo banner, persisted on the user's own document
 * (`dashboard-users-v2/{email}.dismissedBanners`) rather than in localStorage.
 *
 * Why the user doc: a realtor who closes a banner on their laptop expects it to
 * stay closed on their phone, and a "hidden" state that only ever existed in one
 * browser told us nothing about which campaigns people actually reject.
 *
 * Dismissal only. Whether the user already REGISTERED through a banner is not
 * stored here — that lives in the `user-feedbacks` collection, read back via
 * `useHasSubmittedFeedback`.
 */

/**
 * Ids used as keys in `User.dismissedBanners`. Free strings on the schema side
 * (same convention as `UserFeedback.featureTag`), enumerated here so banners
 * can't drift apart on spelling. Never rename an id that has shipped — that
 * resurrects the banner for everyone who already closed it.
 */
export const PROMO_BANNER_IDS = {
  whatsappAiAgent: "whatsapp-ai-agent",
  aiPromo: "ai-promo",
  facebookLeads: "facebook-leads",
  watermark: "watermark",
  makeover: "makeover",
  signatureBeta: "signature-beta",
  crmImprovements: "crm-improvements",
  // The new contacts page (/dashboard/crm) beta launch — the main-page promo card.
  crmBeta: "crm-beta",
  // The feedback strip at the top of the new contacts page itself.
  crmBetaFeedback: "crm-beta-feedback",
  // The personal "bring your office to Webot" offer — same string as its campaign id
  // (AGENCY_AMBASSADOR_CAMPAIGN_ID), so a later round gets a new id and re-shows.
  agencyAmbassador: "agency-ambassador-2026-12",
  // The Facebook group posting beta launch (/dashboard/fb-posting) — the main-page promo card.
  facebookGroupPosting: "facebook-group-posting-beta"
} as const;

export type PromoBannerId = typeof PROMO_BANNER_IDS[keyof typeof PROMO_BANNER_IDS];

export interface PromoBannerState {
  /**
   * The user document has loaded. Banners must render `null` until this is
   * true: unlike localStorage, the dismissal isn't known synchronously, so
   * rendering early flashes a banner the user already closed.
   */
  readonly isReady: boolean;
  /** The user closed this banner (on any device). */
  readonly isDismissed: boolean;
  /** Close the banner — hides it immediately, persists in the background. */
  readonly dismiss: () => void;
}

export function usePromoBanner(bannerId: PromoBannerId): PromoBannerState {
  const { userSettings, updateUserSettings } = useFirestore();

  // Optimistic overlay. The Firestore write is latency-compensated so the
  // snapshot normally lands in the same tick, but this also keeps the banner
  // hidden if the write fails — the user asked for it gone either way.
  const [locallyDismissed, setLocallyDismissed] = useState(false);

  const dismiss = useCallback(() => {
    setLocallyDismissed(true);
    // Merge-set of a nested map: Firestore merges map fields key-by-key, so
    // this never clobbers the other banners' entries.
    updateUserSettings({ dismissedBanners: { [bannerId]: new Date() } }).catch((error) => {
      console.error(`Failed to persist dismissal of banner ${bannerId}`, error);
    });
  }, [bannerId, updateUserSettings]);

  return {
    isReady: Boolean(userSettings),
    isDismissed: locallyDismissed || Boolean(userSettings?.dismissedBanners?.[bannerId]),
    dismiss
  };
}

export default usePromoBanner;
